/**
 * Display label for loop cards/badges.
 *
 * Loop numbering is per-track (migration 590), so a cycle launched with
 * multiple designer paths holds several loops that would all render as
 * "Loop 1". In that case the label is prefixed with the track name
 * ("Cloud Servers — Loop 1"); single-track cycles keep the bare "Loop {n}".
 */

import { useMemo } from 'react';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import type { CatchballLoop } from '../../../types/prioritization/catchballLoop';
import type { Track } from '../../../types/prioritization/track';

type LoopLike = Pick<CatchballLoop, 'cycleId' | 'trackId' | 'loopNumber'>;

export function loopDisplayLabel(
  loop: LoopLike,
  cycleLoops: ReadonlyArray<Pick<CatchballLoop, 'cycleId' | 'trackId'>>,
  tracks: ReadonlyArray<Pick<Track, 'id' | 'name'>>,
): string {
  const base = `Loop ${loop.loopNumber}`;
  const trackIds = new Set<string>();
  for (const l of cycleLoops) {
    if (l.cycleId === loop.cycleId && l.trackId) trackIds.add(l.trackId);
  }
  if (trackIds.size <= 1) return base;
  const trackName = tracks.find((t) => t.id === loop.trackId)?.name;
  return trackName ? `${trackName} — ${base}` : base;
}

/** Store-wired variant for components that render a single loop. */
export function useLoopDisplayLabel(loop: LoopLike | null | undefined): string | null {
  const loops = useCatchballLoopStore((s) => s.loops);
  const tracks = useTrackStore((s) => s.tracks);
  return useMemo(
    () => (loop ? loopDisplayLabel(loop, loops, tracks) : null),
    [loop, loops, tracks],
  );
}
